import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { CoachModal } from '@/features/coach/components/CoachModal';
import { ReferenceVideoCard } from '@/features/feedback/components/ReferenceVideoCard';
import { readReferenceVideo } from '@/features/matches/referenceVideo';
import type { ResourcePin, ResourceReadScope } from '@/lib/types';
import { FeedbackResourcePreview } from './FeedbackResourcePreview';
import { FeedbackPinnedResources } from './FeedbackPinnedResources';
import { useFeedbackParentRead, type FeedbackPinSource } from './useFeedbackPinRead';

export function StaffFeedbackRead(props: { scope: ResourceReadScope; feedbackId: string; onClose(): void }) {
  return <StaffEntry key={JSON.stringify([props.scope, props.feedbackId])} {...props} />;
}
function StaffEntry({ scope, feedbackId, onClose }: { scope: ResourceReadScope; feedbackId: string; onClose(): void }) {
  const [selected, setSelected] = useState<{ source: FeedbackPinSource; pin: ResourcePin } | null>(null);
  const read = useFeedbackParentRead({ purpose: 'staff', scope, feedbackId }, !selected);
  const data = read.data?.schema_version === 'feedback-publication.v1' ? read.data : null;
  const external = data?.external_reference ? readReferenceVideo(data.external_reference) : null;
  return <><CoachModal active={!selected} title={data?.message_excerpt || 'Feedback'} description="Coaches only" size="wide" onClose={onClose}>
    {!read.active || read.error ? <div role="alert"><p>Feedback is unavailable in this context.</p>
      {read.active && <Button variant="outline" onClick={read.refresh}>Retry</Button>}</div>
      : !data ? <p role="status">Loading Feedback…</p>
        : <article className="space-y-5 break-words" aria-label="Staff Feedback">
          <p className="text-xs text-ink-muted">{data.creator_label} · Shared Feedback · version {data.feedback_version}</p>
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{data.message}</p>
          {data.next_step && <div className="rounded-inset bg-field p-3 text-sm"><strong>Next step</strong><p className="whitespace-pre-wrap">{data.next_step}</p></div>}
          <FeedbackPinnedResources resources={data.resource_links}
            source={{ purpose: 'staff', scope, feedbackId, expectedFeedbackRevisionId: data.feedback_revision_id }}
            onPreview={pin => setSelected({ pin, source: { purpose: 'staff', scope, feedbackId, expectedFeedbackRevisionId: data.feedback_revision_id } })} />
          {external && <ReferenceVideoCard video={external} label="External video" />}
          {data.goals.length > 0 && <section className="space-y-2" aria-label="Linked goals"><h4 className="text-sm font-semibold">Linked goals</h4>
            {data.goals.map(goal => <div key={goal.goal_id} className="rounded-inset border border-rule p-3 text-sm"><strong>{goal.title}</strong><span className="ml-2 text-xs text-ink-muted">{goal.status}</span></div>)}
          </section>}
        </article>}
  </CoachModal>
    {selected && <FeedbackResourcePreview source={selected.source} pin={selected.pin} onClose={() => setSelected(null)} />}
  </>;
}
